/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { isEmpty } from 'lodash'
import React, { Component, Fragment } from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import type { WithEuiThemeProps } from '@elastic/eui'
import { EuiFlexGroup, EuiFlexItem, EuiSkeletonText, EuiSpacer, withEuiTheme } from '@elastic/eui'

import ConnectedCCMRolePanel from '@modules/ccm-user-components/rbac/ConnectedCCMRolePanel'
import { isCCMFeatureActivated } from '@modules/ccm-user-lib/feature'
import type { RoleAssignments } from '@modules/cloud-api/v1/types'
import { FormattedMessage } from '@modules/i18n'
import AdminProjectRoleAssignmentsEditor from '@modules/role-assignments-admin-components/ProjectRoleAssignmentsEditor'
import {
  deleteResourcesRoleAssignments,
  getDefaultEsKibanaApiAccessValue,
  isStorageResourcesRoleAssignmentsEmpty,
} from '@modules/role-assignments-lib'
import type { RoleScopeType } from '@modules/role-assignments-lib/types'
import UserProjectRoleAssignmentsEditor from '@modules/role-assignments-user-components/ProjectRoleAssignmentsEditor'
import { deleteCCMRoleAssignments } from '@modules/ccm-user-components/rbac/lib'
import ApiAccessSelector from '@modules/role-assignments-components/ApiAccessSelector'
import type { ApiAccessOptions } from '@modules/role-assignments-components/ApiAccessSelector/types'

import RolePanel from './RolePanel'
import {
  getOrganizationOwnerApiAccess,
  isOrganizationRoleSelected,
  toggleOrganizationBillingRoleAssignment,
  toggleOrganizationOwnerRoleAssignment,
} from './lib'

import type { FC, ReactNode } from 'react'
import type { WrappedComponentProps } from 'react-intl'
import type { AllProps as Props, State } from './types'

const messages = defineMessages({
  organizationOwner: {
    id: 'roles-assignments.organization-owner',
    defaultMessage: 'Organization owner',
  },
  billingAdmin: {
    id: 'roles-assignments.organization-billing',
    defaultMessage: 'Billing admin',
  },
  cloudResourcesAccess: {
    id: 'roles-assignments.instance-access',
    defaultMessage: 'Cloud resource access',
  },
})

class RoleAssignmentsPanel extends Component<
  Props & WithEuiThemeProps & WrappedComponentProps,
  State
> {
  state: State = {
    isInstanceAccessSelected: !isStorageResourcesRoleAssignmentsEmpty(this.props.roleAssignments),
    isCCMPanelEnabled: !isEmpty(this.props.roleAssignments.cloud_connected_resource),
    organizationOwnerApiAccess: getOrganizationOwnerApiAccess(
      this.props.roleAssignments,
      this.props.memberOrApiKey === 'api-key',
    ),
  }

  render() {
    const { roleScopeTypes, roleAssignments, onChangeRoleAssignments } = this.props

    if (roleScopeTypes === null) {
      return <EuiSkeletonText lines={8} />
    }

    return (
      <EuiFlexGroup direction='column' gutterSize='m'>
        {roleScopeTypes.has('organization') && (
          <Fragment>
            <EuiFlexItem>{this.renderOrganizationOwnerPanel()}</EuiFlexItem>
            <EuiFlexItem>{this.renderOrganizationBillingPanel()}</EuiFlexItem>
          </Fragment>
        )}

        <EuiFlexItem>{this.renderInstanceAccessPanel(roleScopeTypes)}</EuiFlexItem>
        <FeatureFlagGatedCCMRolePanel
          initiallyChecked={this.state.isCCMPanelEnabled}
          roleSelectionOverride={isOrganizationRoleSelected(roleAssignments, 'organization-admin')}
          onChangeRoleAssignments={onChangeRoleAssignments}
          roleAssignments={roleAssignments}
          onSwitchToggle={this.onSwitchCCMPanel}
        />
      </EuiFlexGroup>
    )
  }

  renderOrganizationOwnerPanel(): JSX.Element {
    const {
      intl: { formatMessage },
    } = this.props

    const apiAccessSelectorFlexGroupStyle = {
      backgroundColor: this.props.theme.euiTheme.colors.backgroundBaseSubdued,
      border: 0,
      margin: `-${this.props.theme.euiTheme.size.base}!important`,
      borderRadius: this.props.theme.euiTheme.border.radius.medium,
      padding: this.props.theme.euiTheme.size.base,
      gap: this.props.theme.euiTheme.size.base,
    }

    return (
      <RolePanel
        title={formatMessage(messages.organizationOwner)}
        ariaLabel={formatMessage(messages.organizationOwner)}
        description={
          <FormattedMessage
            id='roles-assignments.organization-owner-description'
            defaultMessage='Highest role within the organization. Can manage members, access all features and all resources.'
          />
        }
        isChecked={isOrganizationRoleSelected(this.props.roleAssignments, 'organization-admin')}
        isDisabled={false}
        testId='organization-owner-switch'
        onSwitchToggled={this.onSwitchOrganizationOwner}
      >
        {isOrganizationRoleSelected(this.props.roleAssignments, 'organization-admin') && (
          <ApiAccessSelector
            onChange={this.onSwitchOrganizationOwnerApiAccess}
            defaultValue={this.state.organizationOwnerApiAccess}
            cssStyle={apiAccessSelectorFlexGroupStyle}
            isApiKey={this.props.memberOrApiKey === 'api-key'}
          />
        )}
      </RolePanel>
    )
  }

  renderOrganizationBillingPanel(): JSX.Element {
    const {
      intl: { formatMessage },
    } = this.props

    return (
      <RolePanel
        title={formatMessage(messages.billingAdmin)}
        ariaLabel={formatMessage(messages.billingAdmin)}
        description={
          <FormattedMessage
            id='roles-assignments.organization-billing-description'
            defaultMessage='Access to all invoices and payment methods. Can make subscription changes.'
          />
        }
        isChecked={
          isOrganizationRoleSelected(this.props.roleAssignments, 'billing-admin') ||
          isOrganizationRoleSelected(this.props.roleAssignments, 'organization-admin')
        }
        isDisabled={isOrganizationRoleSelected(this.props.roleAssignments, 'organization-admin')}
        testId='organization-billing-switch'
        onSwitchToggled={this.onSwitchOrganizationBilling}
      />
    )
  }

  renderInstanceAccessPanel(roleScopeTypes: Set<RoleScopeType>): ReactNode {
    const {
      intl: { formatMessage },
    } = this.props

    if (
      !roleScopeTypes.has('deployment') &&
      !roleScopeTypes.has('project-elasticsearch') &&
      !roleScopeTypes.has('project-observability') &&
      !roleScopeTypes.has('project-security')
    ) {
      return null
    }

    const { isInstanceAccessSelected } = this.state

    const isOrganizationOwnerSelected = isOrganizationRoleSelected(
      this.props.roleAssignments,
      'organization-admin',
    )

    return (
      <RolePanel
        title={formatMessage(messages.cloudResourcesAccess)}
        ariaLabel={formatMessage(messages.cloudResourcesAccess)}
        description={
          <FormattedMessage
            id='roles-assignments.instance-access-description'
            defaultMessage='Grant access to one or more cloud resources.'
          />
        }
        isChecked={isOrganizationOwnerSelected || isInstanceAccessSelected}
        isDisabled={isOrganizationOwnerSelected}
        testId='instance-access'
        onSwitchToggled={this.onSwitchInstanceAccess}
      >
        {!isOrganizationOwnerSelected && isInstanceAccessSelected && (
          <Fragment>
            <EuiSpacer size='xl' />
            {roleScopeTypes.has('platform') ? (
              <AdminProjectRoleAssignmentsEditor
                organizationId={this.props.organizationId}
                roleAssignments={this.props.roleAssignments}
                onChangeRoleAssignments={this.props.onChangeRoleAssignments}
                isApiKey={this.props.memberOrApiKey === 'api-key'}
              />
            ) : (
              <UserProjectRoleAssignmentsEditor
                organizationId={this.props.organizationId}
                roleAssignments={this.props.roleAssignments}
                onChangeRoleAssignments={this.props.onChangeRoleAssignments}
                isApiKey={this.props.memberOrApiKey === 'api-key'}
              />
            )}
          </Fragment>
        )}
      </RolePanel>
    )
  }

  onSwitchOrganizationOwner = (): void => {
    const apiAccessValue = getDefaultEsKibanaApiAccessValue(this.props.memberOrApiKey === 'api-key')

    this.setState((prevState) => {
      if (this.isOrganizationOwnerSelected()) {
        return {
          isCCMPanelEnabled: false,
          isInstanceAccessSelected: false,
          organizationOwnerApiAccess: apiAccessValue,
        }
      }

      return prevState
    })

    const roleAssignments = toggleOrganizationOwnerRoleAssignment(
      this.props.roleAssignments,
      this.props.organizationId,
      this.props.memberOrApiKey === 'api-key',
      apiAccessValue,
    )

    this.props.onChangeRoleAssignments(roleAssignments)
  }

  onSwitchOrganizationOwnerApiAccess = (apiAccessValue: ApiAccessOptions): void => {
    const roleAssignments = toggleOrganizationOwnerRoleAssignment(
      this.props.roleAssignments,
      this.props.organizationId,
      this.props.memberOrApiKey === 'api-key',
      apiAccessValue,
      true,
    )

    this.props.onChangeRoleAssignments(roleAssignments)

    this.setState({
      organizationOwnerApiAccess: apiAccessValue,
    })
  }

  onSwitchOrganizationBilling = (): void => {
    const roleAssignments = toggleOrganizationBillingRoleAssignment(
      this.props.roleAssignments,
      this.props.organizationId,
    )

    this.props.onChangeRoleAssignments(roleAssignments)
  }

  onSwitchInstanceAccess = (): void => {
    this.setState(
      (prevState) => ({
        isInstanceAccessSelected: !prevState.isInstanceAccessSelected,
      }),
      () => {
        if (!this.state.isInstanceAccessSelected) {
          const roleAssignments = deleteResourcesRoleAssignments(this.props.roleAssignments)
          this.props.onChangeRoleAssignments(roleAssignments)
        }
      },
    )
  }

  onSwitchCCMPanel = (): void => {
    this.setState(
      (prevState) => ({
        isCCMPanelEnabled: !prevState.isCCMPanelEnabled,
      }),
      () => {
        if (!this.state.isCCMPanelEnabled) {
          const roleAssignments = deleteCCMRoleAssignments(this.props.roleAssignments)
          this.props.onChangeRoleAssignments(roleAssignments)
        }
      },
    )
  }

  isOrganizationOwnerSelected = (): boolean =>
    isOrganizationRoleSelected(this.props.roleAssignments, 'organization-admin')

  isBillingAdminSelected = (): boolean =>
    this.isOrganizationOwnerSelected() ||
    isOrganizationRoleSelected(this.props.roleAssignments, 'billing-admin')
}

const FeatureFlagGatedCCMRolePanel: FC<{
  roleAssignments: RoleAssignments
  initiallyChecked: boolean
  roleSelectionOverride: boolean
  onChangeRoleAssignments: (roleAssignments: RoleAssignments) => void
  onSwitchToggle: () => void
}> = ({
  roleSelectionOverride,
  initiallyChecked,
  roleAssignments,
  onChangeRoleAssignments,
  onSwitchToggle,
}) => {
  if (isCCMFeatureActivated()) {
    return (
      <EuiFlexItem>
        <ConnectedCCMRolePanel
          roleAssignments={roleAssignments}
          initiallyChecked={initiallyChecked}
          roleSelectionOverride={roleSelectionOverride}
          onChangeRoleAssignments={onChangeRoleAssignments}
          onSwitchToggle={onSwitchToggle}
        />
      </EuiFlexItem>
    )
  }

  return null
}

export default injectIntl(withEuiTheme(RoleAssignmentsPanel))
